/**
 * Provenance attachment: stamps partial-translation warnings and
 * confidence annotations onto individual features.
 */

import type { NormalisedFeatureCollection, NormalisedFeature } from '../geojson/index.js';
import type { ExecutionPlan } from '../../planner/index.js';

/**
 * Attach provenance information from the execution plan onto each feature.
 * For partial translations, appends warnings to translation_notes and
 * downgrades mapping_status where appropriate.
 */
export function attachProvenance(
  fc: NormalisedFeatureCollection,
  plan: ExecutionPlan,
): NormalisedFeatureCollection {
  if (plan.kind !== 'partial-translation') return fc;

  const warnings = plan.warnings;
  const unmappedFields = plan.unmappedFields;

  const features = fc.features.map((f): NormalisedFeature => ({
    ...f,
    properties: {
      ...f.properties,
      mapping_status: 'approximate',
      translation_notes: [
        ...f.properties.translation_notes,
        ...warnings,
        unmappedFields.length > 0
          ? `Unmapped ERA fields (not in OSM): ${unmappedFields.join(', ')}`
          : '',
      ].filter(Boolean),
    },
  }));

  return { ...fc, features };
}

/** Structured summary returned by {@link provenanceSummary}. */
export interface ProvenanceSummary {
  /** Average mapping confidence across all features (0–1). */
  avgConfidence: number;
  featureCount: number;
  eraEntity: string;
  source: string;
  partialTranslation: boolean;
  unmappedFields: string[];
  warnings: string[];
  /** Human-readable one-liner for quick display. */
  summary: string;
}

/**
 * Build a structured provenance summary object for UI display.
 */
export function provenanceSummary(fc: NormalisedFeatureCollection): ProvenanceSummary {
  const m = fc.metadata;
  const featureCount = fc.features.length;
  const avgConfidence =
    featureCount === 0
      ? 0
      : fc.features.reduce((sum, f) => sum + (f.properties.mapping_confidence ?? 0), 0) /
        featureCount;

  const summaryLine = [
    `ERA entity: ${m.queried_era_entity}`,
    `Source: ${m.source_system}`,
    `Features: ${featureCount}`,
    m.partial_translation ? `⚠ Partial translation — unmapped: ${m.unmapped_fields.join(', ')}` : '',
    ...m.warnings,
  ]
    .filter(Boolean)
    .join(' | ');

  return {
    avgConfidence,
    featureCount,
    eraEntity: m.queried_era_entity,
    source: m.source_system,
    partialTranslation: m.partial_translation,
    unmappedFields: m.unmapped_fields,
    warnings: m.warnings,
    summary: summaryLine,
  };
}
